import d3 from 'd3';
import _ from 'lodash';
import $ from 'jquery';

import BaseFacetView from './BaseFacetView';

export default class PeriodicFacetView extends BaseFacetView {

  constructor(config, $el, margins) {
    super(config, $el, margins);
    this._title = 'Periodic Chart';

    this._innerRadius = '15%';
    this._outerRadius = '30%';
    this._labelPadding = 20;
    this._spacing = 0.05;
    this._spacingBg = 0;
  }

  _initGetSetters() {
    this.innerRadius = (value) => this._getSetter(value, '_innerRadius', this._updateElements);
    this.outerRadius = (value) => this._getSetter(value, '_outerRadius', this._updateElements);
    this.labelPadding = (value) => this._getSetter(value, '_labelPadding', this._updateElements);
    super._initGetSetters();
  }

  _resolveMeasure(val) {
    if (val[val.length - 1] !== '%') return parseInt(val, 10);
    return (d3.min([this._height, this._width]) / 100) * parseInt(val, 10);
  }

  // override
  _updateGroups() {
    this._g = this._updateMainGroup();
    this._bgGroup = this._updateBackgroundGroup(this._g);
    this._dataGroup = this._updateDataGroup(this._g);
    this._hitGroup = this._updateHitGroup(this._g);
    this._labelsGroup = this._updateLabelsGroup(this._g);
  }

  // override
  _updateMainGroup() {
    this._svg = this._div.selectAll('svg').data(['svg']);
    this._svg.enter().append('svg').append('g');
    this._svg.attr('width', this._width + this._margins.left + this._margins.right)
        .attr('height', this._height + this._margins.top + this._margins.bottom);

    const group = this._svg.select('g');
    const leftT = (this._margins.left + this._margins.right) / 2 + (this._width / 2);
    const topT = this._margins.top + (this._height / 2);
    group.attr('transform', `translate(${leftT}, ${topT})`);
    return group;
  }

  _updateBackgroundGroup(parent) {
    const group = parent.selectAll('g.bg').data(['g']);
    group.enter().append('g')
      .attr('class', 'bg');
    return group;
  }

  _updateDataGroup(parent) {
    const group = parent.selectAll('g.data').data(['g']);
    group.enter().append('g')
      .attr('class', 'data');
    return group;
  }

  _updateHitGroup(parent) {
    const group = parent.selectAll('g.hit').data(['g']);
    group.enter().append('g')
      .attr('class', 'hit')
      .style('cursor', 'crosshair')
      .on('mouseleave', (d) => this._onMouseLeave(d));
    return group;
  }

  _updateLabelsGroup(parent) {
    const group = parent.selectAll('g.labels').data(['g']);
    group.enter().append('g')
      .attr('class', 'labels')
      .style('pointer-events', 'none');
    group.attr('transform', 'translate(0 1)');
    return group;
  }

  // override
  _updateScales() {
    if (!this._xValue || !this._yValue || !this._data) return;
    const inner = this._resolveMeasure(this._innerRadius);
    const outer = this._resolveMeasure(this._outerRadius);

    this._xExtent = d3.extent(this._data, this._xValue);
    this._xScale = this._xScaleFunction
      .domain(this._xExtent)
      .rangeRound([0, 360]);

    this._yMax = d3.max(this._data, this._yValue);
    this._yScale = this._yScaleFunction
      .domain([0, this._yMax])
      .rangeRound([inner, outer - 3]);

    this._drawChart();
  }

  // override
  _drawChart() {
    this._drawBackground();
    this._drawData();
    this._drawHit();
    this._drawLabels();
  }

  // override
  _drawBackground() {
    this._bgPie = d3.layout.pie()
      .sort(null)
      .value(() => 1);

    const bgArc = d3.svg.arc()
      .padAngle(this._spacingBg)
      .innerRadius(this._resolveMeasure(this._innerRadius))
      .outerRadius(this._resolveMeasure(this._outerRadius));

    const bgbars = this._bgGroup.selectAll('path')
      .data(this._bgPie(this._data), d => this._xValue(d.data));

    bgbars.enter()
      .append('path');

    bgbars.attr('d', bgArc)
      .style('fill', d => this._isSelected(d) ? this._selectedUpColorBg : this._upColorBg);

    bgbars.exit().remove();
  }

  // override
  _drawHit() {
    this._hitPie = d3.layout.pie()
      .sort(null)
      .value(() => 1);

    const hitArc = d3.svg.arc()
      .innerRadius(this._resolveMeasure(0))
      .outerRadius(this._resolveMeasure(this._outerRadius));

    const hitbars = this._hitGroup.selectAll('path')
      .data(this._hitPie(this._data), d => this._xValue(d.data));

    hitbars.enter()
      .append('path')
      .style('stroke-width', 0)
      .style('fill', 'transparent')
      .on('mouseover', d => this._onMouseOver(d))
      .on('mouseout', d => this._onMouseOut(d))
      .on('mousedown', d => this._onMouseDown(d))
      .on('mouseup', d => this._onMouseUp(d));

    hitbars.attr('d', hitArc);

    hitbars.exit().remove();
  }


  // override
  _drawData() {
    this._pie = d3.layout.pie()
      .sort(null)
      .padAngle(this._spacing)
      .value(() => 1);

    const arc = d3.svg.arc()
      .innerRadius(this._resolveMeasure(this._innerRadius))
      .outerRadius(d => this._yMap(d.data));

    const bars = this._dataGroup.selectAll('path')
      .data(this._pie(this._data), d => this._xValue(d.data));

    bars.enter()
      .append('path')
      .style('pointer-events', 'none');

    bars.attr('d', arc)
      .style('fill', d => this._isSelected(d) ? this._selectedUpColor : this._upColor);

    bars.exit().remove();
  }

  // override
  _drawLabels() {
    const labelsArc = d3.svg.arc()
      .innerRadius(this._resolveMeasure(this._outerRadius))
      .outerRadius(this._resolveMeasure(this._outerRadius) + this._labelPadding);

    const labels = this._labelsGroup.selectAll('text').data(this._pie(this._data));

    labels.enter()
      .append('text')
      .attr('class', 'periodiclabel')
      .style('alignment-baseline', 'middle')
      .style('text-anchor', 'middle');

    labels
      .text(d => this._xLabel(d.data))
      .attr('transform', d => `translate(${labelsArc.centroid(d)})`);

    labels.exit().remove();
  }

  _colorSector(d, color, bgColor) {
    bgColor = bgColor || color;
    this._dataGroup.selectAll('path')
      .data(this._pie([d.data]), dd => this._xValue(dd.data))
      .style('fill', color);
    this._bgGroup.selectAll('path')
      .data(this._bgPie([d.data]), dd => this._xValue(dd.data))
      .style('fill', bgColor);
  }

  _crossProduct(v1, v2) {
    return (v1[0] * v2[1]) - (v1[1] * v2[0]);
  }

  _dotProduct(v1, v2) {
    return (v1[0] * v2[0]) + (v1[1] * v2[1]);
  }

  clear() {
    this._updateSelection(null);
    super.clear();
  }

  _getCenter() {
    const pLeft = parseInt(this._div.style('padding-left'), 10);
    const pTop = parseInt(this._div.style('padding-top'), 10);
    return {
      x: $(this._svg.node()).offset().left + this._width / 2 + this._margins.left + pLeft,
      y: $(this._svg.node()).offset().top + this._height / 2 + this._margins.top + pTop,
    };
  }

  _getPos() {
    return [d3.event.pageX - this._center.x, d3.event.pageY - this._center.y];
  }

  // override
  _onMouseDown(d) {
    this._direction = 0;
    this._center = this._getCenter();
    this._prevPos = this._getPos();
    this._mouseDown = true;
    this._clickedSelection = this._isSelected(d);
    this._startDrag = this._xValue(d.data);
    this._endDrag = this._xValue(d.data);
    this._selectedExtent = [this._startDrag, this._endDrag];
    this._onMouseOver(d);
  }

  // override
  _onMouseUp() {
    this._mouseDown = false;
    if (this._clickedSelection && this._startDrag === this._endDrag) {
      this._startDrag = undefined;
      this._endDrag = undefined;
    }
    if (!this._liveUpdate) this._onExtentSelected();
    this._clickedSelection = false;
  }

  // override
  _onMouseOver(d) {
    if (this._mouseDown) {
      const cross = this._crossProduct(this._prevPos, this._getPos());
      const dot = this._dotProduct(this._prevPos, this._getPos());
      this._direction += Math.atan2(cross, dot);
      this._direction = this._direction % (2 * Math.PI);
      console.log(this._direction);
      this._prevPos = this._getPos();
      this._endDrag = this._xValue(d.data);
      this._selectedExtent = this._direction > 0 ? [this._startDrag, this._endDrag] : [this._endDrag, this._startDrag];
      this._drawSelection(d, 'down');
      if (this._liveUpdate) this._onExtentSelected();
    } else {
      this._drawSelection(d, 'over');
    }
  }

  // override
  _onMouseOut(d) {
    this._drawSelection(d, 'up');
  }

  // override
  _onMouseLeave() {
    if (this._mouseDown) {
      d3.select('body').on('mouseup', () => {
        this._onMouseUp();
        d3.select('body').on('mouseup', null);
      });
    }
  }

  _onExtentSelected() {
    if (this._startDrag === undefined || this._endDrag === undefined) {
      this._facet.filter(null);
    } else {
      this._facet.filter(this._selectedExtent);
    }
  }

  // override
  _isSelected(d) {
    if (!this._selectedExtent || this._selectedExtent.length < 2) return false;
    const start = parseInt(this._selectedExtent[0], 10);
    const end = parseInt(this._selectedExtent[1], 10);
    const val = this._xValue(d.data);
    if (start <= end) return start <= val && val <= end;
    return val <= end || val >= start;
  }

  _updateSelection(extent) {
    if (!extent || !extent.length) this._selectedExtent = undefined;
    else this._selectedExtent = extent;
  }

  _drawSelection(d, state) {
    let color;
    if (state === 'over') color = this._isSelected(d) ? '_selectedOverColor' : '_overColor';
    else if (state === 'down') color = this._isSelected(d) ? '_selectedDownColor' : '_downColor';
    else if (state === 'up') color = this._isSelected(d) ? '_selectedUpColor' : '_upColor';

    this._dataGroup.selectAll('path')
      .data(this._pie(this._data), dd => this._xValue(dd.data))
      .style('fill', dd => this._isSelected(dd) ? this._selectedUpColor : this._upColor);
    this._dataGroup.selectAll('path')
      .data([d], dd => this._xValue(dd.data))
      .style('fill', this[color]);

    this._bgGroup.selectAll('path')
      .data(this._bgPie(this._data), dd => this._xValue(dd.data))
      .style('fill', dd => this._isSelected(dd) ? this._selectedUpColorBg : this._upColorBg);
    this._bgGroup.selectAll('path')
      .data([d], dd => this._xValue(dd.data))
      .style('fill', this[`${color}Bg`]);
  }

}



/** WEBPACK FOOTER **
 ** D:/2016/2016 - ISI/grantorino/frontend/../../../../Marco/Dropbox (ISI Foundation)/2016 - ISI/grantorino/frontend/src/js/views/sidebar/archive/facets/PeriodicFacetView.es6
 **/